<template>
  <div class="opportunity-detail">
    <!-- Not-found state for unresolved ids — keeps the UX predictable when
         the backend isn't reachable or an admin pastes a stale URL. -->
    <b-card v-if="notFound" class="text-center py-5">
      <i class="icon-magnifier text-muted" style="font-size: 2.5rem;"></i>
      <h4 class="mt-3">Opportunity not found</h4>
      <p class="text-muted mb-3">
        We couldn't load this opportunity. It may have been removed or you
        may be on a different community.
      </p>
      <b-button variant="primary" @click="goBackToList">
        Back to Opportunities
      </b-button>
    </b-card>

    <!-- Loaded state -->
    <template v-else-if="opportunity">
      <!-- Header card: title, company, meta pills, status badge, plus the
           admin actions (Approve / Reject while in_review, Change status,
           Edit). -->
      <b-card class="mb-3 opportunity-header">
        <div class="d-flex justify-content-between align-items-start flex-wrap opportunity-header__row">
          <div class="opportunity-header__main">
            <h2 v-if="!editing" class="opportunity-header__title">{{ opportunity.title }}</h2>
            <b-form-input
              v-else
              v-model="draft.title"
              class="edit-input edit-input--title"
              placeholder="Opportunity title"
            />
            <h5 v-if="!editing" class="opportunity-header__company">
              {{ opportunity.company }}
            </h5>
            <b-form-input
              v-else
              v-model="draft.company"
              class="edit-input edit-input--company mt-2"
              placeholder="Company"
            />
          </div>
          <div class="opportunity-header__status">
            <!-- Static mode: status action buttons (target states only) + Edit -->
            <template v-if="!editing">
              <span
                :class="[
                  'status-pill',
                  'status-pill--header',
                  `status-pill--${opportunity.status}`,
                ]"
              >
                <span class="status-pill__dot"></span>
                <span class="text-capitalize">
                  {{ statusLabel(opportunity.status) }}
                </span>
              </span>
              <b-dropdown
                right
                boundary="viewport"
                variant="btn btn-dropbox tab-focus"
                class="action-status-dropdown ml-2"
                :aria-label="`Actions for ${opportunity.title}`"
              >
                <template slot="button-content">
                  <i class="icon-settings"></i>
                </template>
                <b-dropdown-item
                  v-for="a in statusActions"
                  :key="a.value"
                  @click="applyStatusChange(a.value)"
                >
                  {{ a.label }} Opportunity
                </b-dropdown-item>
              </b-dropdown>
              <b-button
                variant="dropbox"
                class="ml-2 opportunity-header__edit-btn"
                @click="enterEdit"
              >
                <i class="fa fa-pencil-alt mr-2" aria-hidden="true"></i>
                Edit Opportunity
              </b-button>
            </template>
            <!-- Editing mode: [Cancel] + [Save] (only when changes exist) -->
            <template v-else>
              <b-button
                variant="outline-danger"
                :disabled="saving"
                @click="cancelEdit"
              >
                Cancel
              </b-button>
              <b-button
                v-if="hasChanges"
                variant="success"
                class="ml-2"
                :disabled="saving"
                @click="saveEdit"
              >
                <i v-if="saving" class="fa fa-spinner fa-spin mr-2" aria-hidden="true"></i>
                {{ saving ? 'Saving…' : 'Save' }}
              </b-button>
            </template>
          </div>
        </div>
      </b-card>


      <!-- Metrics row — computed from persistedMatches (see `metrics` computed). -->
      <div v-if="metrics === null" class="metrics-loading text-center py-4">
        <i class="fa fa-spinner fa-spin text-muted"></i>
        <span class="text-muted ml-2">Loading metrics…</span>
      </div>
      <div
        v-else-if="
          metrics.deliveredCount === 0 &&
          metrics.openCount === 0 &&
          metrics.clickCount === 0 &&
          metrics.notInterestedCount === 0 &&
          metrics.feedbackCount === 0 &&
          metrics.referralCount === 0 &&
          metrics.unsubscribeCount === 0 &&
          metrics.appliedCount === 0 &&
          metrics.securedCount === 0
        "
        class="metrics-zero text-center py-4"
      >
        <h5 class="mb-2">No performance data yet</h5>
        <p class="mb-1 text-muted">
          Once this opportunity is routed to matched alumni, you'll see delivery,
          click, and engagement counts here.
        </p>
        <p class="small mb-0 click-definition">
          Clicks count alumni who tapped the Apply link from their match
          email — not opportunity views.
        </p>
      </div>
      <template v-else>
        <!-- Funnel row — 6 KPIs on one row. Referrals hidden for now (metric still
             computed below; re-add its card to restore). -->
        <div class="row metrics-row">
          <div class="col-md-2 mb-3">
            <b-card class="metric-card">
              <div class="metric-card__value">
                {{ metrics.deliveredCount }}
              </div>
              <div class="metric-card__label">
                Alumni Contacted
                <i
                  v-b-tooltip.hover.top="
                    'Alumni who received a match email for this opportunity.'
                  "
                  class="fa fa-question-circle metric-card__info"
                  aria-label="More info"
                ></i>
              </div>
            </b-card>
          </div>
          <div class="col-md-2 mb-3">
            <b-card class="metric-card">
              <div class="metric-card__value">
                {{ metrics.openCount }}
              </div>
              <div class="metric-card__label">
                Email Opens
                <i
                  v-b-tooltip.hover.top="
                    'Alumni who opened their match email at least once.'
                  "
                  class="fa fa-question-circle metric-card__info"
                  aria-label="More info"
                ></i>
              </div>
            </b-card>
          </div>
          <div class="col-md-2 mb-3">
            <b-card class="metric-card">
              <div class="metric-card__value">
                {{ metrics.clickCount }}
              </div>
              <div class="metric-card__label">
                Application Clicks
                <i
                  v-b-tooltip.hover.top="
                    'Alumni who clicked the Apply link in their match email.'
                  "
                  class="fa fa-question-circle metric-card__info"
                  aria-label="More info"
                ></i>
              </div>
            </b-card>
          </div>
          <div class="col-md-2 mb-3">
            <b-card class="metric-card">
              <div class="metric-card__value">
                {{ metrics.notInterestedCount }}
              </div>
              <div class="metric-card__label">
                Not Interested
                <i
                  v-b-tooltip.hover.top="
                    'Alumni who clicked \'Not Interested\' in the match email.'
                  "
                  class="fa fa-question-circle metric-card__info"
                  aria-label="More info"
                ></i>
              </div>
            </b-card>
          </div>
          <div class="col-md-2 mb-3">
            <b-card class="metric-card">
              <div class="metric-card__value">
                {{ metrics.feedbackCount }}
              </div>
              <div class="metric-card__label">
                Feedback
                <i
                  v-b-tooltip.hover.top="
                    'Alumni who submitted feedback about this opportunity.'
                  "
                  class="fa fa-question-circle metric-card__info"
                  aria-label="More info"
                ></i>
              </div>
            </b-card>
          </div>
          <div class="col-md-2 mb-3">
            <b-card class="metric-card">
              <div class="metric-card__value">
                {{ metrics.unsubscribeCount }}
              </div>
              <div class="metric-card__label">
                Unsubscribed
                <i
                  v-b-tooltip.hover.top="
                    'Alumni who unsubscribed from match emails through this opportunity.'
                  "
                  class="fa fa-question-circle metric-card__info"
                  aria-label="More info"
                ></i>
              </div>
            </b-card>
          </div>
        </div>
        <!-- Outcomes row — bottom-of-funnel. Rendered only when at least one is > 0
             so the "aspirational" metrics don't clutter the layout before endpoints ship. -->
        <div
          v-if="metrics.appliedCount > 0 || metrics.securedCount > 0"
          class="row metrics-row metrics-row--secondary"
        >
          <div class="col-md-6 mb-3">
            <b-card class="metric-card metric-card--secondary">
              <div class="metric-card__value">
                {{ metrics.appliedCount }}
              </div>
              <div class="metric-card__label">
                Applied
                <i
                  v-b-tooltip.hover.top="
                    'Alumni who confirmed they applied to this opportunity.'
                  "
                  class="fa fa-question-circle metric-card__info"
                  aria-label="More info"
                ></i>
              </div>
            </b-card>
          </div>
          <div class="col-md-6 mb-3">
            <b-card class="metric-card metric-card--secondary">
              <div class="metric-card__value">
                {{ metrics.securedCount }}
              </div>
              <div class="metric-card__label">
                Secured
                <i
                  v-b-tooltip.hover.top="
                    'Alumni who landed the role for this opportunity.'
                  "
                  class="fa fa-question-circle metric-card__info"
                  aria-label="More info"
                ></i>
              </div>
            </b-card>
          </div>
        </div>
      </template>

      <div class="row tab-body">
            <!-- LEFT column: Job Details + Required Skills + Description. -->
            <div class="col-md-8">
              <b-card class="mb-3 section-card">
                <div class="section-title">Job Details</div>
                <div class="row">
                  <div class="col-md-6 field-cell">
                    <div class="field-label">Employer</div>
                    <div v-if="!editing" class="field-value">
                      {{ display(opportunity.company) }}
                    </div>
                    <b-form-input v-else v-model="draft.company" placeholder="Employer" />
                  </div>
                  <div class="col-12 field-cell">
                    <div class="field-label">Application URL</div>
                    <div v-if="!editing" class="field-value">
                      <a
                        v-if="opportunity.applicationUrl"
                        :href="externalUrl(opportunity.applicationUrl)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="field-value__link"
                      >
                        {{ opportunity.applicationUrl }}
                      </a>
                      <span v-else>{{ EMPTY }}</span>
                    </div>
                    <b-form-input
                      v-else
                      v-model="draft.applicationUrl"
                      type="url"
                      placeholder="https://…"
                    />
                  </div>
                </div>
              </b-card>

              <b-card class="mb-3 section-card">
                <div class="section-title">Required Skills</div>
                <template v-if="!editing">
                  <div
                    v-if="opportunity.skills && opportunity.skills.length"
                    class="skills-chips"
                  >
                    <span
                      v-for="skill in opportunity.skills"
                      :key="skill"
                      class="skill-chip"
                    >
                      {{ skill }}
                    </span>
                  </div>
                  <p v-else class="text-muted mb-0 empty-state">
                    No skills specified.
                  </p>
                </template>
                <b-form-tags
                  v-else
                  v-model="draft.skills"
                  placeholder="Add a skill and press Enter"
                  remove-on-delete
                />
              </b-card>

              <b-card class="mb-3 section-card">
                <div class="section-title">Description</div>
                <template v-if="!editing">
                  <div
                    v-if="opportunity.description"
                    class="description-scroll"
                  >
                    {{ opportunity.description }}
                  </div>
                  <p v-else class="text-muted mb-0 empty-state">
                    No description provided.
                  </p>
                </template>
                <b-form-textarea
                  v-else
                  v-model="draft.description"
                  rows="6"
                  max-rows="14"
                  placeholder="Opportunity description…"
                />
              </b-card>
            </div>

            <!-- RIGHT column: Key Details first, aligned under the header actions (SCRUM-679). -->
            <div class="col-md-4">
              <b-card class="mb-3 section-card">
                <div class="section-title">Key Details</div>
                <div class="posting-info">
                  <div class="field-cell">
                    <div class="field-label">Location</div>
                    <div v-if="!editing" class="field-value">
                      {{ display(opportunity.location) }}
                    </div>
                    <div v-else class="edit-location">
                      <div class="row">
                        <div class="col-6">
                          <b-form-input
                            v-model="draft.locationStruct.city"
                            placeholder="City"
                          />
                        </div>
                        <div class="col-6">
                          <b-form-input
                            v-model="draft.locationStruct.state"
                            placeholder="State"
                          />
                        </div>
                      </div>
                      <b-form-checkbox
                        v-model="draft.locationStruct.remote"
                        class="mt-2"
                      >
                        Remote
                      </b-form-checkbox>
                    </div>
                  </div>
                  <div class="field-cell">
                    <div class="field-label">Job Type</div>
                    <div v-if="!editing" class="field-value">
                      {{ display(opportunity.jobType) }}
                    </div>
                    <b-form-select
                      v-else
                      v-model="draft.type"
                      :options="jobTypeOptions"
                    />
                  </div>
                  <div class="field-cell">
                    <div class="field-label">Industry</div>
                    <div v-if="!editing" class="field-value">
                      {{ displayList(opportunity.industry) }}
                    </div>
                    <b-form-tags
                      v-else
                      v-model="draft.industry"
                      placeholder="Add industry and press Enter"
                      remove-on-delete
                    />
                  </div>
                  <div class="field-cell">
                    <div class="field-label">Salary</div>
                    <div v-if="!editing" class="field-value">
                      {{ formattedSalary || EMPTY }}
                    </div>
                    <div v-else class="edit-salary">
                      <div class="row">
                        <div class="col-6">
                          <b-form-input
                            v-model="draft.salary.start"
                            type="number"
                            placeholder="Min"
                          />
                        </div>
                        <div class="col-6">
                          <b-form-input
                            v-model="draft.salary.end"
                            type="number"
                            placeholder="Max"
                          />
                        </div>
                      </div>
                      <b-form-select
                        v-model="draft.salary.period"
                        :options="[
                          { value: 'year', text: 'per year' },
                          { value: 'month', text: 'per month' },
                          { value: 'hour', text: 'per hour' },
                        ]"
                        class="mt-2"
                      />
                    </div>
                  </div>
                  <div class="field-cell">
                    <div class="field-label">Required Education</div>
                    <div v-if="!editing" class="field-value">
                      {{ displayEducation(opportunity.requiredEducation) }}
                    </div>
                    <b-form-select
                      v-else
                      v-model="draft.requiredEducation"
                      :options="educationOptions"
                    />
                  </div>
                  <div class="field-cell">
                    <div class="field-label">Required Experience</div>
                    <div v-if="!editing" class="field-value">
                      {{
                        displayExperience(opportunity.requiredExperienceYears)
                      }}
                    </div>
                    <b-form-input
                      v-else
                      v-model="draft.requiredExperienceYears"
                      type="number"
                      min="0"
                      placeholder="Years"
                    />
                  </div>
                </div>
              </b-card>

              <b-card v-if="false" class="mb-3 section-card">
                <div class="section-title">Submitted By</div>
                <template v-if="hasSubmitter">
                  <div class="submitter-row">
                    <div class="submitter-avatar">{{ submitterInitials }}</div>
                    <div class="submitter-text">
                      <div class="submitter-name">
                        {{ opportunity.source.firstName }}
                        {{ opportunity.source.lastName }}
                      </div>
                      <div class="submitter-email">
                        {{ opportunity.source.email }}
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else class="empty-state">
                  <p class="text-muted mb-1">{{ EMPTY }}</p>
                  <p class="text-muted small mb-0">
                    Bulk-ingested
                  </p>
                </div>
              </b-card>

              <b-card class="mb-3 section-card">
                <div class="section-title">Posting Info</div>
                <div class="posting-info">
                  <div class="field-cell">
                    <div class="field-label">Date Submitted</div>
                    <div class="field-value">
                      {{ displayDate(opportunity.submittedDate) }}
                    </div>
                  </div>
                  <div class="field-cell">
                    <div class="field-label">Expiration Date</div>
                    <div v-if="!editing" class="field-value">
                      {{ displayDay(expirationOf(opportunity)) }}
                    </div>
                    <b-form-input
                      v-else
                      v-model="draft.expiration"
                      type="date"
                    />
                  </div>
                  <div class="field-cell">
                    <div class="field-label">Submitted Via</div>
                    <div class="field-value">
                      {{ displaySourceChannel(opportunity.source) }}
                    </div>
                  </div>
                </div>
              </b-card>
            </div>
          </div>
          <b-row>
            <b-col md="12">
              <b-card>
                <b-card-body>
                  <h3>Sent <span v-text="`(${alumniMatched.length})`"></span></h3>
                  <b-table id="alumni-matched" hover :items="alumniMatched" :fields="alumniMatchedColumns">
                    <template v-slot:cell(name)="row">
                      <span class="display-link" @click="goToProfile(row.item._memberId)" v-if="row.item._memberId">
                        {{ row.item.name }}
                      </span>
                      <span v-else>{{ row.item.name }}</span>
                    </template>
                    <template v-slot:cell(score)="row">
                      {{ row.item.score + '%' }}
                    </template>
                    <template v-slot:cell(matchReasons)="row">
                      <span v-if="row.item.matchReasons && row.item.matchReasons.length">
                        {{ row.item.matchReasons.join(', ') }}
                      </span>
                      <span v-else>N/A</span>
                    </template>
                    <template v-slot:cell(status)="row">
                      <span :class="[
                        { 'md-orange-color': row.item.status === 'pending_send' || row.item.status === 'pending' },
                        { 'md-green-color': row.item.status === 'approved' || row.item.status === 'sent' },
                        { 'md-yellow-color': row.item.status === 'opened' },
                        { 'md-blue-color': row.item.status === 'clicked' },
                      ]">
                        {{ statusMatchLabel(row.item.status) }}
                      </span>
                    </template>
                  </b-table>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <!-- Feedback — derived from the same fetch, filtered to matches with -->
          <b-row>
            <b-col md="12">
              <b-card>
                <b-card-body>
                  <h3>Feedback <span v-text="`(${feedbackRows.length})`"></span></h3>
                  <b-table id="opp-feedback" hover :items="feedbackRows" :fields="feedbackColumns">
                    <template v-slot:cell(name)="row">
                      <span class="display-link" @click="goToProfile(row.item._memberId)" v-if="row.item._memberId">
                        {{ row.item.name }}
                      </span>
                      <span v-else>{{ row.item.name }}</span>
                    </template>
                    <template v-slot:cell(comment)="row">
                      <span v-if="row.item.comment">{{ row.item.comment }}</span>
                      <span v-else class="text-muted">No comment.</span>
                    </template>
                    <template v-slot:cell(rating)="row">
                      <span class="feedback-stars" :aria-label="`${row.item.rating} of 5 stars`">
                        <span
                          v-for="n in 5"
                          :key="n"
                          :class="['feedback-star', n <= row.item.rating ? 'feedback-star--filled' : '']"
                        >★</span>
                      </span>
                    </template>
                  </b-table>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <!-- Activity — mocked while backend endpoint is being finalised. -->
          <b-row>
            <b-col md="12">
              <b-card>
                <b-card-body>
                  <h3>Activity <span v-text="`(${opportunityActivity.length})`"></span></h3>
                  <v-client-table
                    :data="opportunityActivity"
                    :columns="activityColumns"
                    :options="activityOptions"
                    id="dataTable"
                    class="request-activity-table"
                  >
                    <span slot="date" slot-scope="props" v-if="props.row.date">
                      {{ props.row.date | momentDate }}
                    </span>
                    <span slot="date" v-else>N/A</span>
                    <span
                      slot="memberName"
                      slot-scope="props"
                      v-if="props.row.memberRel && props.row.memberRel._id"
                      class="display-link"
                      @click="goToProfile(props.row.memberRel._id)"
                    >
                      {{ props.row.memberName }}
                    </span>
                    <span slot="memberName" slot-scope="props" v-else>
                      {{ props.row.memberName }}
                    </span>
                    <span slot="type" slot-scope="props" v-if="props.row.type"
                      :class="[
                        { 'badge badge-warning': props.row.type === 'Email' },
                        { 'badge badge-danger': props.row.type === 'Feedback' },
                        { 'badge badge-info': props.row.type === 'Update' },
                      ]">
                      {{ props.row.type }}
                    </span>
                  </v-client-table>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
    </template>

    <!-- Loading state — brief, while the lookup (mock or real) resolves. -->
    <b-card v-else class="text-center py-5">
      <i class="fa fa-spinner fa-spin text-muted" style="font-size: 1.5rem;"></i>
      <p class="mt-2 mb-0 text-muted">Loading opportunity…</p>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
var moment = require('moment-timezone')
import {
  getMockOpportunityById,
  getMockMetricsForOpportunity,
  setMockOpportunityStatus,
} from './mocks/opportunities'
import {
  CORE_API_URL,
  STATUS_ACTIONS,
  coreApiOpportunityToDashboard,
} from './opportunityAdapter'

import EventBus from '../../assets/js/EventBus'

// VUE_APP_USE_OPPORTUNITIES_MOCKS=true falls back to the in-memory mocks.
// Default talks to core-api at VUE_APP_CORE_API_URL.
const USE_MOCKS = process.env.VUE_APP_USE_OPPORTUNITIES_MOCKS === 'true'

const EMPTY = '—'

const EDUCATION_LABELS = {
  'no-degree': 'No Degree',
  'high-school': 'High School diploma',
  associates: "Associate's degree",
  bachelors: "Bachelor's degree",
  masters: "Master's degree",
}

const EDUCATION_OPTIONS = [
  { value: null, text: 'Not specified' },
  { value: 'no-degree', text: 'No Degree' },
  { value: 'high-school', text: 'High School diploma' },
  { value: 'associates', text: "Associate's degree" },
  { value: 'bachelors', text: "Bachelor's degree" },
  { value: 'masters', text: "Master's degree" },
]

const JOB_TYPE_OPTIONS = [
  { value: null, text: 'Not specified' },
  { value: 'full-time', text: 'Full-Time' },
  { value: 'part-time', text: 'Part-Time' },
  { value: 'hourly', text: 'Hourly' },
  { value: 'contract', text: 'Contract' },
  { value: 'internship', text: 'Internship' },
]

// Wire-side value → normalized dashboard "jobType" label used by the adapter.
const TYPE_LABEL_TO_WIRE = {
  'Full-Time': 'full-time',
  'Part-Time': 'part-time',
  Hourly: 'hourly',
  Contract: 'contract',
  Internship: 'internship',
}

const SOURCE_LABELS = {
  'program-page': 'Program Page',
  dashboard: 'Dashboard',
  bulk: 'Bulk (job-board ingestion)',
}

export default {
  name: 'OpportunityDetail',
  data() {
    return {
      opportunity: null,
      notFound: false,
      token: '',
      EMPTY,
      // metrics is now a computed derived from persistedMatches (see computed block).
      // No more loading/error data props — the loading state is `metrics === null`,
      // which happens only while persistedMatches hasn't fetched yet.
      closeModalOpen: false,
      closeSubmitting: false,
      // Edit mode: `draft` is a clone of `opportunity`; save PATCHes only changed keys.
      editing: false,
      draft: null,
      saving: false,
      educationOptions: EDUCATION_OPTIONS,
      jobTypeOptions: JOB_TYPE_OPTIONS,
      // Persisted matches for the Alumni Matched + Feedback tables.
      // null while first fetch is in flight (so metrics computed shows a loading
      // state). Populated to an array (possibly empty) after loadPersistedMatches resolves.
      persistedMatches: null,
      alumniMatchedColumns: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'score', label: 'Score', sortable: true },
        { key: 'matchReasons', label: 'Match Reasons', sortable: false },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
      ],
      feedbackColumns: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'comment', label: 'Comment', sortable: false },
        { key: 'rating', label: 'Rating', sortable: true },
      ],
      // Column shape mirrors ask activity (RequestDetail.vue) so the two screens read the same.
      activityColumns: ['date', 'memberName', 'type', 'typeDetail'],
      activityOptions: {
        sortable: ['date', 'memberName', 'type', 'typeDetail'],
        sortIcon: { base: 'fa', up: 'fa-sort-asc', down: 'fa-sort-desc', is: 'fa-sort' },
        headings: { typeDetail: 'Type Detail', memberName: 'Member' },
        pagination: { chunk: 10, nav: 'fixed' },
      },
    }
  },
  async created() {
    this.token = localStorage.getItem('token')
    const id = this.$route.params.opportunity_id

    // Route guard: bounce to /dashboard if Recruiting is disabled for this community.
    const activeCommunity =
      this.$route.params.community_id ||
      this.$route.query.community_id ||
      localStorage.getItem('user_modal_option')
    if (activeCommunity && activeCommunity !== 'all') {
      const enabled = await this.isRecruitingEnabled(activeCommunity)
      if (!enabled) {
        this.$router.replace('/dashboard')
        return
      }
    }

    if (USE_MOCKS) {
      const opp = getMockOpportunityById(id)
      if (!opp) {
        this.notFound = true
        return
      }
      this.opportunity = opp
      this.applyBreadcrumbTitle()
      // Mocks flow: no persisted matches to fetch — set to empty array so
      // the `metrics` computed exits its null (loading) state.
      this.persistedMatches = []
      return
    }

    try {
      const authToken = localStorage.getItem('id_token')
      const { data } = await axios.get(
        `${CORE_API_URL}/api/opportunities/${id}`,
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
      )
      const adapted = coreApiOpportunityToDashboard(data)
      if (!adapted) {
        this.notFound = true
        return
      }
      this.opportunity = adapted
      this.applyBreadcrumbTitle()
      this.loadPersistedMatches()
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem('authUser')
        this.$router.push('/login')
        return
      }
      this.notFound = true
    }
  },
  computed: {
    statusActions() {
      // Same STATUS_ACTIONS as the list; the opp's current status is filtered out.
      if (!this.opportunity) return STATUS_ACTIONS
      return STATUS_ACTIONS.filter((a) => a.value !== this.opportunity.status)
    },
    formattedSalary() {
      const s = this.opportunity && this.opportunity.salary
      if (!s || (s.start == null && s.end == null)) return ''
      const k = (n) =>
        n != null ? `$${Math.round(n / 1000)}k` : '—'
      return `${k(s.start)} – ${k(s.end)}`
    },
    hasSubmitter() {
      const src = this.opportunity && this.opportunity.source
      return !!(src && src.firstName && src.lastName)
    },
    submitterInitials() {
      if (!this.hasSubmitter) return ''
      const f = this.opportunity.source.firstName.charAt(0)
      const l = this.opportunity.source.lastName.charAt(0)
      return `${f}${l}`.toUpperCase()
    },
    // True when any editable field on `draft` diverges from `opportunity`.
    hasChanges() {
      if (!this.editing || !this.draft || !this.opportunity) return false
      const keys = [
        'title',
        'company',
        'type',
        'applicationUrl',
        'description',
        'requiredEducation',
        'requiredExperienceYears',
      ]
      for (const k of keys) {
        if ((this.draft[k] || null) !== (this.opportunity[k] || null)) return true
      }
      if ((this.draft.expiration || null) !== this.toDateInput(this.expirationOf(this.opportunity))) return true
      if (JSON.stringify(this.draft.industry || []) !== JSON.stringify(this.opportunity.industry || [])) return true
      if (JSON.stringify(this.draft.skills || []) !== JSON.stringify(this.opportunity.skills || [])) return true
      if (JSON.stringify(this.draft.locationStruct || null) !== JSON.stringify(this.opportunity.locationStruct || null)) return true
      if (JSON.stringify(this.draft.salary || null) !== JSON.stringify(this.opportunity.salary || null)) return true
      return false
    },
    // Rows for the Alumni Matched table — only post-send statuses are surfaced.
    alumniMatched() {
      const POST_SEND_STATUSES = new Set([
        'sent', 'reminder_sent', 'clicked', 'not_interested',
        'referred', 'applied', 'secured', 'bounced',
        'unsubscribed', 'suppressed', 'expired',
      ])
      return (this.persistedMatches || [])
        .filter((m) => {
          if (!m) return false
          if (POST_SEND_STATUSES.has(m.status)) return true
          if (m.status === 'pending_send' && m.email && m.email.mercuryId) return true
          return false
        })
        .map((m) => this.buildMatchRow(m))
    },
    // Metrics computed from persistedMatches (the match documents themselves)
    metrics() {
      // Null while the initial fetch hasn't landed yet — template renders a loading state.
      if (!this.persistedMatches) return null
      const rows = this.persistedMatches.filter(Boolean)
      const count = (predicate) => rows.filter(predicate).length
      const hasEngagement = (m, field) => m.engagement && m.engagement[field]
      const POST_SEND_STATUSES_NON_BOUNCED = new Set([
        'sent', 'reminder_sent', 'clicked', 'not_interested',
        'referred', 'applied', 'secured',
        'unsubscribed', 'suppressed', 'expired',
      ])
      // Delivered = post-send status (minus bounces) OR Mercury already dispatched
      // (mercuryId set) even if the webhook hasn't confirmed the transition yet.
      const isDelivered = (m) => {
        if (POST_SEND_STATUSES_NON_BOUNCED.has(m.status)) return true
        if (m.status === 'pending_send' && m.email && m.email.mercuryId) return true
        return false
      }
      return {
        deliveredCount: count(isDelivered),
        openCount: count((m) => hasEngagement(m, 'openedAt')),
        clickCount: count((m) => hasEngagement(m, 'clickedAt') || m.status === 'clicked'),
        notInterestedCount: count((m) => hasEngagement(m, 'notInterestedAt') || m.status === 'not_interested'),
        feedbackCount: count((m) => hasEngagement(m, 'feedback')),
        referralCount: count((m) => hasEngagement(m, 'referredAt') || m.status === 'referred'),
        unsubscribeCount: count((m) => m.status === 'unsubscribed'),
        appliedCount: count((m) => hasEngagement(m, 'appliedAt') || m.status === 'applied'),
        securedCount: count((m) => hasEngagement(m, 'securedAt') || m.status === 'secured'),
      }
    },
    // Rows for the Feedback table — only matches that captured feedback.
    feedbackRows() {
      return (this.persistedMatches || [])
        .filter((m) => m && m.engagement && m.engagement.feedback)
        .map((m) => {
          const row = this.buildMatchRow(m)
          const fb = m.engagement.feedback || {}
          return {
            _memberId: row._memberId,
            name: row.name,
            comment: fb.note || '',
            rating: Number(fb.rating) || 0,
          }
        })
    },
    // Unified activity feed — merges opportunity-level activityLog (opp created,
    // status changes)
    opportunityActivity() {
      const oppEntries = (this.opportunity && this.opportunity.activityLog) || []
      const oppRows = oppEntries.map((e) => this.buildActivityRow(e, null))
      const matchRows = (this.persistedMatches || []).flatMap((m) => {
        const memberCtx = this.extractMemberContext(m)
        // "match created" is an algorithm event (a candidate was generated), not an
        // admin action — hide it so Activity only shows real events (SCRUM-678).
        return (m.activityLog || [])
          .filter((e) => e && e.action !== 'match_created')
          .map((e) => this.buildActivityRow(e, memberCtx))
      })
      return [...oppRows, ...matchRows].sort((a, b) => {
        const at = a._sortAt || 0
        const bt = b._sortAt || 0
        return bt - at
      })
    },
  },
  methods: {
    externalUrl(url) {
      // Scheme-less URLs would resolve relative to the dashboard instead of the employer site.
      return /^https?:\/\//i.test(url) ? url : `https://${url}`
    },
    // Community-level toggle used by the created() guard for direct URL entries.
    async isRecruitingEnabled(communityId) {
      try {
        const { data } = await axios.get(
          `${CORE_API_URL}/api/recruiting/${communityId}`,
        )
        return data && data.enabled === true
      } catch (err) {
        return false
      }
    },
    // Open the member profile in a new tab — same UX as advisors in RequestDetail.vue.
    goToProfile(id) {
      if (!id) return
      const route = this.$router.resolve({ path: `/engagement/members/${id}` })
      window.open(route.href, '_blank')
    },
    // Human labels for the Alumni Matched status column.
    statusMatchLabel(status) {
      const labels = {
        pending_send: 'Pending Send',
        pending: 'Pending',
        approved: 'Approved',
        sent: 'Sent',
        opened: 'Opened',
        clicked: 'Clicked',
        not_interested: 'Not Interested',
        applied: 'Applied',
        secured: 'Secured',
        bounced: 'Bounced',
        unsubscribed: 'Unsubscribed',
        suppressed: 'Suppressed',
        expired: 'Expired',
      }
      return labels[status] || status || 'N/A'
    },
    // Extract the member sliver (id + display name) from a persisted match doc
    // so activity rows attributed to that match can show the alumnus's name.
    extractMemberContext(m) {
      const populated = m && m.member && typeof m.member === 'object'
      const memberId = populated ? m.member._id : (m && m.member)
      const first = populated ? (m.member.first || '') : ''
      const last = populated ? (m.member.last || '') : ''
      const displayName = populated ? (m.member.displayName || '') : ''
      const name = [first, last].filter(Boolean).join(' ').trim() || displayName || ''
      return { memberId, name }
    },
    // Map a backend activityLog entry into the row shape the v-client-table expects.
    // memberCtx is provided only for match-level entries; opp-level entries fall back
    // to the entry's own memberName (usually "system" for backend-generated events).
    buildActivityRow(entry, memberCtx) {
      const when = entry && entry.when ? new Date(entry.when) : null
      const rawName = memberCtx && memberCtx.name
        ? memberCtx.name
        : ((entry && entry.memberName) || '')
      const memberName = rawName && rawName.toLowerCase() === 'system' ? 'System' : (rawName || 'System')
      const { type, typeDetail } = this.mapActivity(entry)
      return {
        _sortAt: when ? when.getTime() : 0,
        _memberId: memberCtx && memberCtx.memberId ? memberCtx.memberId : null,
        date: when,
        memberName,
        memberRel: memberCtx && memberCtx.memberId ? { _id: memberCtx.memberId } : null,
        type,
        typeDetail,
      }
    },
    // Translate a backend action + description into the UI's (type, typeDetail) pair.
    // Keeps the badge palette consistent with RequestDetail.vue's activity table.
    mapActivity(entry) {
      const action = (entry && entry.action) || ''
      const description = (entry && entry.description) || ''
      const details = (entry && entry.details) || ''
      const event = (entry && entry.event) || {}
      if (action === 'match_status_changed') {
        const newStatus = (description.split('to ')[1] || '').trim()
        return { type: 'Email', typeDetail: this.statusMatchLabel(newStatus) || 'Status changed' }
      }
      if (action === 'match_created') {
        const pct = event.similarity != null ? `${Math.round(event.similarity * 100)}%` : ''
        return { type: 'Email', typeDetail: pct ? `Matched (${pct})` : 'Matched' }
      }
      if (action === 'feedback_submitted') {
        const rating = event.rating != null ? `Rated ${event.rating}/5` : 'Feedback submitted'
        return { type: 'Feedback', typeDetail: details ? `${rating} — ${details}` : rating }
      }
      if (action === 'opportunity_created') {
        // Bulk-source opps came from CSV import; keep the label honest.
        const channel = this.opportunity && this.opportunity.source && this.opportunity.source.channel
        const detail = channel === 'bulk' ? 'Created via CSV import' : 'Opportunity created'
        return { type: 'Update', typeDetail: detail }
      }
      if (action === 'status_changed') {
        return { type: 'Update', typeDetail: description || 'Status changed' }
      }
      if (action === 'opportunity_deleted') {
        return { type: 'Update', typeDetail: 'Opportunity deleted' }
      }
      return { type: 'Update', typeDetail: description || action || '—' }
    },
    // Shared row shape for Alumni Matched + Feedback; falls back if populate misses.
    buildMatchRow(m) {
      const populated = m.member && typeof m.member === 'object'
      const memberId = populated ? m.member._id : m.member
      const first = populated ? (m.member.first || '') : ''
      const last = populated ? (m.member.last || '') : ''
      const displayName = populated ? (m.member.displayName || '') : ''
      const name = [first, last].filter(Boolean).join(' ').trim() || displayName || 'Alumnus'
      // Match reasons mirror hermes: top 3 skills of the alumnus + first industry.
      const skills = populated && m.member.dimensions && Array.isArray(m.member.dimensions.skills)
        ? m.member.dimensions.skills.slice(0, 3)
        : []
      const industry = populated && Array.isArray(m.member.industry) ? m.member.industry.slice(0, 1) : []
      return {
        _id: m._id,
        _memberId: memberId,
        name,
        score: Math.round(((m.similarity || 0)) * 100),
        matchReasons: skills.concat(industry),
        email: (populated && Array.isArray(m.member.emails) && m.member.emails[0]) || '',
        status: this.deriveDisplayStatus(m),
      }
    },
    // Sent-table status: highest state reached wins (Clicked/Not Interested → Opened
    // → Sent). Opened = engagement.openedAt; Delivered omitted; special states pass through.
    deriveDisplayStatus(m) {
      const status = m.status || 'pending'
      const eng = m.engagement || {}
      const SPECIAL = new Set([
        'bounced', 'unsubscribed', 'suppressed', 'expired',
        'referred', 'applied', 'secured',
      ])
      if (SPECIAL.has(status)) return status
      if (status === 'clicked' || eng.clickedAt) return 'clicked'
      if (status === 'not_interested' || eng.notInterestedAt) return 'not_interested'
      if (eng.openedAt) return 'opened'
      // Baseline: sent/dispatched (pending_send + mercuryId). Else fall back to raw status.
      const dispatched = m.email && m.email.mercuryId
      if (status === 'sent' || status === 'reminder_sent' || (status === 'pending_send' && dispatched)) {
        return 'sent'
      }
      return status
    },
    // Fetch persisted matches for the Alumni Matched + Feedback tables; non-fatal on failure.
    async loadPersistedMatches() {
      const id = this.$route.params.opportunity_id
      try {
        const authToken = localStorage.getItem('id_token')
        const { data } = await axios.get(
          `${CORE_API_URL}/api/opportunity-matches/by-opportunity/${id}`,
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
        )
        this.persistedMatches = Array.isArray(data) ? data : []
      } catch (e) {
        this.persistedMatches = []
      }
    },
    // Map internal status → admin label; active/rejected read as Approved/Rejected.
    statusLabel(status) {
      const labels = {
        in_review: 'In Review',
        active: 'Approved',
        closed: 'Closed',
        rejected: 'Rejected',
        incomplete: 'Incomplete',
        quarantined: 'Quarantined',
      }
      return labels[status] || status
    },
    // No-op wrapper kept because two lifecycle hooks reference it (created + status
    // change reload). Metrics now derive from persistedMatches via the `metrics`
    // computed, so this only needs to trigger a re-fetch of matches.
    async loadMetrics() {
      if (USE_MOCKS) return
      // persistedMatches drives the metrics computed; re-hydrate it after any
      // action that could have changed match state.
      await this.loadPersistedMatches()
    },
    async applyStatusChange(newStatus) {
      if (!newStatus || newStatus === this.opportunity.status) return

      if (USE_MOCKS) {
        const updated = setMockOpportunityStatus(
          this.$route.params.opportunity_id,
          newStatus,
        )
        if (updated) {
          this.opportunity.status = updated.status
          this.opportunity.closeDate = updated.closeDate
          this.opportunity.rejectionDate = updated.rejectionDate
        }
        return
      }

      try {
        const authToken = localStorage.getItem('id_token')
        const { data } = await axios.patch(
          `${CORE_API_URL}/api/opportunities/${this.$route.params.opportunity_id}`,
          { status: newStatus },
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
        )
        const adapted = coreApiOpportunityToDashboard(data) || {}
        this.opportunity.status = adapted.status || newStatus
        if (adapted.closeDate) this.opportunity.closeDate = adapted.closeDate
        if (adapted.rejectionDate)
          this.opportunity.rejectionDate = adapted.rejectionDate
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('authUser')
          this.$router.push('/login')
          return
        }
        alert(
          'We couldn\'t update this opportunity\'s status. Please try again in a moment.',
        )
      }
    },
    applyBreadcrumbTitle() {
      const matched = this.$route.matched
      const leaf = matched && matched[matched.length - 1]
      if (leaf && this.opportunity && this.opportunity.title) {
        leaf.name = this.opportunity.title
        EventBus.$emit('breadcrumb:refresh')
      }
    },
    display(value) {
      if (value === null || value === undefined || value === '') return EMPTY
      return value
    },
    displayList(arr) {
      if (!arr || !arr.length) return EMPTY
      return arr.join(', ')
    },
    displayDate(value) {
      if (!value) return EMPTY
      return moment(value).format('MMMM D, YYYY')
    },
    // Date-only fields are stored as UTC midnight; formatting in local time shows the day before.
    displayDay(value) {
      if (!value) return EMPTY
      return moment.utc(value).format('MMMM D, YYYY')
    },
    // Same fallback as Hermes: legacy/URL-import opps only carry `deadline`.
    expirationOf(opportunity) {
      return opportunity.deadline || opportunity.expirationDate || null
    },
    toDateInput(value) {
      return value ? moment.utc(value).format('YYYY-MM-DD') : null
    },
    displayEducation(value) {
      if (!value) return EMPTY
      return EDUCATION_LABELS[value] || value
    },
    displayExperience(years) {
      if (years === null || years === undefined) return EMPTY
      if (years === 0) return 'No prior experience required'
      if (years === 1) return '1 year'
      return `${years} years`
    },
    displaySourceChannel(source) {
      if (!source || !source.channel) return EMPTY
      return SOURCE_LABELS[source.channel] || source.channel
    },
    goBackToList() {
      this.$router.push('/engagement/opportunities')
    },
    // ─── Edit mode ───
    enterEdit() {
      if (!this.opportunity) return
      // Deep clone so field edits don't mutate the live opportunity until
      // save() succeeds. JSON round-trip is fine — no functions or Dates.
      this.draft = JSON.parse(JSON.stringify(this.opportunity))
      this.draft.expiration = this.toDateInput(this.expirationOf(this.opportunity))
      this.editing = true
    },
    cancelEdit() {
      if (this.saving) return
      if (this.hasChanges) {
        const ok = window.confirm(
          'Discard your changes? Unsaved edits will be lost.',
        )
        if (!ok) return
      }
      this.draft = null
      this.editing = false
    },
    // Build a partial PATCH payload with only the fields that diverge from
    // the server-side opportunity. Keeps the wire minimal and avoids
    // clobbering fields we never rendered.
    buildPatchPayload() {
      const d = this.draft
      const o = this.opportunity
      const payload = {}
      if ((d.title || '') !== (o.title || '')) payload.title = d.title
      if ((d.company || '') !== (o.company || '')) payload.company = d.company
      if ((d.description || '') !== (o.description || '')) payload.description = d.description
      if ((d.applicationUrl || '') !== (o.applicationUrl || '')) payload.applicationURL = d.applicationUrl
      if ((d.expiration || null) !== this.toDateInput(this.expirationOf(o))) {
        // Keep `deadline` in sync so the display fallback doesn't hide the edit; the auto-close cron reads `expirationDate`.
        payload.expirationDate = d.expiration || null
        if (o.deadline) payload.deadline = d.expiration || null
      }
      if ((d.requiredEducation || null) !== (o.requiredEducation || null)) payload.requiredEducation = d.requiredEducation || null
      if ((d.requiredExperienceYears || null) !== (o.requiredExperienceYears || null)) {
        payload.requiredExperienceYears = d.requiredExperienceYears == null ? null : Number(d.requiredExperienceYears)
      }
      // The <b-form-select> is bound to draft.type (wire enum) directly, so
      // we forward it as-is. `jobType` is the display label the adapter fills
      // for read-only rendering — we don't send it to the backend.
      if ((d.type || null) !== (o.type || null)) {
        payload.type = d.type || null
      }
      if (JSON.stringify(d.industry || []) !== JSON.stringify(o.industry || [])) {
        payload.industry = d.industry || []
      }
      if (JSON.stringify(d.skills || []) !== JSON.stringify(o.skills || [])) {
        payload.skills = d.skills || []
      }
      if (JSON.stringify(d.locationStruct || null) !== JSON.stringify(o.locationStruct || null)) {
        payload.location = d.locationStruct || null
      }
      if (JSON.stringify(d.salary || null) !== JSON.stringify(o.salary || null)) {
        const s = d.salary || {}
        payload.salary = {
          min: s.start == null ? null : Number(s.start),
          max: s.end == null ? null : Number(s.end),
          currency: s.currency || 'USD',
          period: s.period || 'year',
          disclosed: s.disclosed == null ? null : !!s.disclosed,
        }
      }
      return payload
    },
    async saveEdit() {
      if (!this.hasChanges || this.saving) return
      const payload = this.buildPatchPayload()
      if (Object.keys(payload).length === 0) return

      this.saving = true

      if (USE_MOCKS) {
        // Merge into the local opportunity + exit — the mocks store is
        // in-memory so a next reload still shows the pre-edit values.
        // That's fine for the demo path.
        Object.keys(this.draft).forEach((k) => { this.opportunity[k] = this.draft[k] })
        this.applyBreadcrumbTitle()
        this.editing = false
        this.draft = null
        this.saving = false
        return
      }

      try {
        const authToken = localStorage.getItem('id_token')
        const { data } = await axios.patch(
          `${CORE_API_URL}/api/opportunities/${this.$route.params.opportunity_id}`,
          payload,
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
        )
        const adapted = coreApiOpportunityToDashboard(data)
        if (adapted) {
          this.opportunity = adapted
          this.applyBreadcrumbTitle()
        }
        this.editing = false
        this.draft = null
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('authUser')
          this.$router.push('/login')
          return
        }
        alert(
          "We couldn't save your changes. Please check the fields and try again.",
        )
      } finally {
        this.saving = false
      }
    },
  },
  filters: {
    momentDate(date) {
      return date ? moment(date).format('MMM D, YYYY') : ''
    },
  },
}
</script>

<style scoped>
/* Edit-mode inputs. Match the size of the static text they replace so
   the layout doesn't jump when toggling edit on/off. */
.edit-input--title >>> .form-control {
  font-size: 1.75rem;
  font-weight: 600;
  padding: 0.35rem 0.6rem;
}
.edit-input--company >>> .form-control {
  font-size: 1.1rem;
  font-weight: 500;
}
/* Status action buttons and Edit share one size so the header row reads as a
   uniform set of equal buttons. */
.opportunity-header__edit-btn,
.opportunity-header__action-btn {
  white-space: nowrap;
  min-width: 100px;
  padding: 0.5rem 1rem;
  font-weight: 500;
}
/* Every element in the header row (status pill, gear dropdown, action buttons
   and Edit) shares ONE exact height so the row reads as a uniform set. */
.opportunity-header__status .status-pill--header,
.opportunity-header__status .action-status-dropdown > .btn,
.opportunity-header__edit-btn,
.opportunity-header__action-btn {
  height: 35px;
  padding-top: 0;
  padding-bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
/* The status pill reads a touch narrow next to the buttons — give it a bit
   more breathing room on the sides. */
.opportunity-header__status .status-pill--header {
  padding-left: 22px;
  padding-right: 22px;
  min-width: 130px;
  justify-content: center;
}

/* Dark-theme fix: <b-form-select> renders a native <select> and its default
   browser chevron blends into the dark background. Overlay our own so users
   can see which fields are dropdowns. Same treatment applied elsewhere via
   scoped >>> since bootstrap-vue puts the class inside a child element. */
.section-card >>> .custom-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none'><path d='M1 1L6 6L11 1' stroke='%239aa0a6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 12px 8px;
  padding-right: 2.25rem;
}
/* ─── Header card ────────────────────────────────────────────────── */
.opportunity-header__main {
  flex: 1;
  min-width: 0;
}
.opportunity-header__title {
  margin-bottom: 0.25rem;
  font-weight: 700;
  overflow-wrap: break-word;
}
.opportunity-header__company {
  color: var(--text-muted, #9aa0a6);
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 0;
}
.opportunity-header__status {
  flex-shrink: 0;
  margin-left: 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 0.5rem;
}

/* Responsive */
@media (max-width: 991px) {
  .opportunity-header__row {
    flex-direction: column;
  }
  .opportunity-header__main {
    width: 100%;
  }
  .opportunity-header__status {
    width: 100%;
    margin-left: 0;
    margin-top: 1rem;
    justify-content: flex-start;
  }
  .opportunity-header__status .btn {
    min-width: 0;
    flex: 1 1 auto;
  }
  .opportunity-header__status > .btn:first-child {
    margin-left: 0 !important;
  }
}

/* ─── Status pill (used in header + posting-info) ────────────────── */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid transparent;
}
/* Header variant matches the sibling action buttons (height set below). */
.status-pill--header {
  padding: 0 16px;
  font-size: 0.9rem;
}
.status-pill__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.status-pill--active {
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.35);
  color: #4ade80;
}
.status-pill--in_review {
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.35);
  color: #fbbf24;
}
.status-pill--closed {
  background: rgba(148, 163, 184, 0.15);
  border-color: rgba(148, 163, 184, 0.35);
  color: #cbd5e1;
}
.status-pill--rejected {
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.35);
  color: #f87171;
}
.status-pill--incomplete {
  background: rgba(56, 189, 248, 0.12);
  border-color: rgba(56, 189, 248, 0.35);
  color: #38bdf8;
}
.status-pill--quarantined {
  background: rgba(71, 85, 105, 0.2);
  border-color: rgba(71, 85, 105, 0.4);
  color: #94a3b8;
}
.section-card {
  border: none !important;
}
.section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted, #9aa0a6);
  margin-bottom: 1rem;
}

/* ─── Field grid (Company / Location / etc. inside a section) ──────── */
.field-cell {
  margin-bottom: 1.25rem;
}
.field-cell:last-child {
  margin-bottom: 0;
}
.field-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted, #9aa0a6);
  margin-bottom: 0.25rem;
}
.field-value {
  font-size: 0.95rem;
  word-break: break-word;
}
.field-value__link {
  color: #38bdf8;
  text-decoration: none;
}
.field-value__link:hover {
  text-decoration: underline;
}

/* ─── Required-skills chips (purple, matches brand) ────────────────── */
.skills-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.skill-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 6px;
  background: rgba(139, 92, 246, 0.15);
  border: 1px solid rgba(139, 92, 246, 0.35);
  color: #c4b5fd;
  font-size: 0.85rem;
  font-weight: 500;
}

/* ─── Submitter block (avatar + name + email) ───────────────────────── */
.submitter-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.submitter-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #8b5cf6;
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  flex-shrink: 0;
}
.submitter-text {
  min-width: 0;
}
.submitter-name {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.1rem;
}
.submitter-email {
  font-size: 0.85rem;
  color: var(--text-muted, #9aa0a6);
  word-break: break-word;
}

/* ─── Empty-state copy (no skills / no description / bulk submitter) ── */
.empty-state {
  font-size: 0.9rem;
}

/* ─── Description scroll-internal (long copy doesn't stretch page) ──── */
.description-scroll {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 0.5rem;
  white-space: pre-wrap;
  line-height: 1.65;
  font-size: 0.95rem;
}
.tab-body.row {
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.tab-body.row > [class*='col-'] {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.tab-body.row > .col-md-8 {
  padding-right: 0.5rem !important;
}
.tab-body.row > .col-md-4 {
  padding-left: 0.5rem !important;
}

/* ─── matched + click KPI cards (inline, no tab) ───────── */
.metrics-row {
  margin-left: 0;
  margin-right: 0;
}
.metrics-row > [class*='col-'] {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
.metrics-row > [class*='col-']:first-child {
  padding-left: 0;
}
.metrics-row > [class*='col-']:last-child {
  padding-right: 0;
}
.metric-card {
  border: none !important;
  height: 100%;
}
::v-deep .metric-card > .card-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: 90px;
}
/* Secondary rows sit BELOW the hero — smaller vertical footprint so the page
   doesn't feel top-heavy when all 6+ cards render. */
.metrics-row--secondary {
  margin-top: -0.5rem;
}
::v-deep .metric-card--secondary > .card-body {
  min-height: 72px;
  padding: 0.75rem 1rem;
}
.metric-card--secondary .metric-card__value {
  font-size: 1.75rem;
}
.metric-card--secondary .metric-card__label {
  font-size: 0.75rem;
  letter-spacing: 0.03em;
}
.metric-card__value {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  margin-bottom: 0.5rem;
}
.metric-card__label {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.7);
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.metric-card__info {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.45);
  cursor: help;
  transition: color 0.15s ease;
}
.metric-card__info:hover {
  color: rgba(255, 255, 255, 0.85);
}
.metrics-zero h5 {
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
}
.metrics-zero .click-definition {
  margin-top: 0.75rem;
  font-style: italic;
  opacity: 0.75;
}
/* Feedback rating stars — same look-and-feel as hermes RecruitingDetail. */
.feedback-stars {
  display: inline-flex;
  gap: 2px;
  color: #d1d5db;
  font-size: 16px;
  line-height: 1;
}
.feedback-star--filled {
  color: #f59e0b;
}
</style>
